import React, { useState, useRef, useEffect } from "react";
import {
  Heart,
  MessageCircle,
  Send,
  Bookmark,
  MoreHorizontal,
  BookOpen,
  Flag,
  Trash2,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import SourceInfoModal from "./SourceInfoModal";
import ContextMenu from "./ContextMenu";
import { toast } from "sonner";
import { getAuthHeader } from "../services/authToken";
import { useTranslation } from "react-i18next";

const InstagramCarousel = ({
  post,
  entity,
  onLike,
  onSave,
  onComment,
  onProfileClick,
  onPostDeleted,
  setShowSettings,
  setCurrentTab,
}) => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [liked, setLiked] = useState(post.liked_by_user || false);
  const [saved, setSaved] = useState(post.saved_by_user || false);
  const [sourceModalOpen, setSourceModalOpen] = useState(false);
  const [showPostMenu, setShowPostMenu] = useState(false);
  const scrollRef = useRef(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showFeedback, setShowFeedback] = useState({});
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Helper function to extract character names from content
  function extractEntityFromContent(content) {
    if (!content) return null;

    const patterns = [
      /^([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)\s*:/, // "Romeo:", "Juliet Smith:"
      /said\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/, // "said Romeo"
      /([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)\s+(?:says|asked|replied)/, // "Romeo says"
    ];

    for (const pattern of patterns) {
      const match = content.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    return null;
  }

  // Enhanced entity with proper fallback chain including chapter/section profiles
  const extractedName = extractEntityFromContent(post.content);
  // Build a safe display entity name that avoids showing technical IDs like "section::..."
  const resolveDisplayName = () => {
    const raw = post.entity_persona;
    if (typeof raw === "string") {
      // Handle hierarchical IDs like "chapter::instanceId::0001" or "section::instanceId::0001.0001"
      const sectionMatch = raw.match(
        /^(chapter|section)::[^:]+::(\d+)\.?([0-9]+)?$/
      );
      if (sectionMatch) {
        const [, type, chapterNum, sectionNum] = sectionMatch;
        const chapter = parseInt(chapterNum, 10);
        if (type === "chapter") return `${t('navigation.chapters')} ${chapter}`;
        if (sectionNum)
          return `${t('navigation.chapters')} ${chapter} - ${t('carousel.section_label', { num: parseInt(sectionNum, 10) })}`;
      }
      // If it's not a hierarchical token, use as-is
      if (!/^\w+::/.test(raw)) return raw;
    }
    // Fallbacks
    return (
      post.character_name ||
      post.speaker ||
      extractedName ||
      post.position_data?.chapter_title ||
      post.toc_chapter_title ||
      (post.id === "test_manual_456"
        ? "Romeo"
        : post.id === "test_content_1"
        ? "Juliet"
        : post.id === "test_content_2"
        ? "Mercutio"
        : post.id === "test_content_3"
        ? "Friar Lawrence"
        : "Content")
    );
  };

  const enhancedEntity = entity || {
    id: post.user_id,
    name: resolveDisplayName(),
    avatar_url: post.entity_avatar_url || "",
    verified: false,
  };

  const slides = post.slides || [];
  const totalSlides = slides.length;

  // Helper to get safe display title - never show technical IDs
  const getDisplayTitle = () => {
    const rawTitle = post.title;

    // Check if title looks like a technical ID (section::..., chapter::..., etc.)
    if (
      !rawTitle ||
      (typeof rawTitle === "string" &&
        /^(section|chapter|entity)::/i.test(rawTitle))
    ) {
      // Try to get title from first slide
      if (slides.length > 0 && slides[0].title) {
        return slides[0].title;
      }
      // Final fallback
      return t('common.done'); // Using 'done' or similar as generic title placeholder
    }

    return rawTitle;
  };

  // Sync local saved state with post prop when it changes
  useEffect(() => {
    setSaved(post.saved_by_user || false);
  }, [post.saved_by_user]);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (isScrolling) return;

      const scrollLeft = scrollContainer.scrollLeft;
      const slideWidth = scrollContainer.offsetWidth;
      const newSlideIndex = Math.round(scrollLeft / slideWidth);

      if (newSlideIndex !== currentSlide) {
        setCurrentSlide(newSlideIndex);
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [currentSlide, isScrolling]);

  const scrollToSlide = (index) => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    setIsScrolling(true);
    const slideWidth = scrollContainer.offsetWidth;
    scrollContainer.scrollTo({
      left: slideWidth * index,
      behavior: "smooth",
    });

    // Reset scrolling flag after animation
    setTimeout(() => setIsScrolling(false), 300);
  };

  const handleLike = () => {
    setLiked(!liked);
    onLike?.(post.id, !liked);
  };

  const handleSave = () => {
    setSaved(!saved);
    onSave?.(post.id, !saved);
  };

  const handleDeletePost = async () => {
    if (!post?.id) return;
    try {
      setIsDeleting(true);
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL ||
        (import.meta?.env && import.meta.env.REACT_APP_BACKEND_URL) ||
        "http://localhost:8002";
      const resp = await fetch(`${backendUrl}/api/posts/${post.id}`, {
        method: "DELETE",
        headers: getAuthHeader(),
      });

      if (!resp.ok) throw new Error(t('carousel.post_delete_failed'));

      toast.success(t('carousel.post_deleted'));
      onPostDeleted?.(post.id);
      setShowDeleteConfirm(false);
      setShowPostMenu(false);
    } catch (err) {
      console.error("Error deleting post:", err);
      toast.error(t('carousel.post_delete_failed'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleQuizAnswer = (slideId, selectedIndex, quizData) => {
    // Store the selected answer
    setQuizAnswers((prev) => ({
      ...prev,
      [slideId]: selectedIndex,
    }));

    // Show feedback after a short delay
    setTimeout(() => {
      setShowFeedback((prev) => ({
        ...prev,
        [slideId]: true,
      }));

      // Validate answer against source
      const isCorrect = selectedIndex === quizData.correctAnswer;

      // Optional: Could add analytics or scoring here
      console.log(`Quiz answer for ${slideId}:`, {
        selectedIndex,
        correctIndex: quizData.correctAnswer,
        isCorrect,
        explanation: quizData.explanation,
        sourceRef: post.source_ref,
      });
    }, 300);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));

    if (hours < 24) {
      return `${hours}h`;
    } else {
      const days = Math.floor(hours / 24);
      return `${days}d`;
    }
  };

  const SlideContent = ({ slide, slideIndex }) => {
    const slideType = slide.type || "concept";

    if (slideType === "image" && slide.image_url) {
      return (
        <div className="w-full h-full bg-gray-100 flex items-center justify-center">
          <img
            src={slide.image_url}
            alt={slide.title || `Slide ${slideIndex + 1}`}
            className="max-w-full max-h-full object-cover"
          />
        </div>
      );
    }

    // Text-based slide (concept, quiz, resource) - 3:4 responsive layout compact sizing
    return (
      <div
        className={`w-full h-full bg-gradient-to-br from-purple-50 to-blue-50 p-4 flex flex-col ${
          slideType === "quiz" ? "justify-start" : "justify-center"
        }`}
      >
        {slideType === "quiz" && (
          <div className="mb-2">
            <span className="bg-orange-100 text-orange-800 text-xs font-medium px-2 py-1 rounded-full">
              {t('carousel.quiz.question_label')}
            </span>
          </div>
        )}

        <h3
          className={`font-bold text-gray-900 mb-3 leading-tight ${
            slideType === "quiz" ? "text-lg" : "text-lg"
          }`}
        >
          {slideType === "quiz"
            ? slide.title || t('carousel.quiz.default_title')
            : slide.title || `${t('common.done')} ${slideIndex + 1}`}
        </h3>

        {slideType === "quiz" && slide.quiz?.question && (
          <div className="text-gray-700 text-sm leading-relaxed mb-3">
            {slide.quiz.question}
          </div>
        )}

        {slideType !== "quiz" && (
          <div className="text-gray-700 text-sm leading-relaxed">
            {slide.content || slide.explanation || slide.description}
          </div>
        )}

        {slideType === "quiz" && slide.quiz?.options && (
          <div className="mt-2 flex-1 flex flex-col min-h-0">
            {(() => {
              const slideId = slide.id || `slide-${slideIndex}`;

              return (
                <>
                  <div className="space-y-2 flex-1 min-h-0">
                    {slide.quiz.options.map((option, idx) => {
                      const isSelected = quizAnswers[slideId] === idx;
                      const isCorrect = slide.quiz.correctAnswer === idx;
                      const showResult = showFeedback[slideId];

                      let optionClass =
                        "p-2.5 border rounded-lg cursor-pointer transition-all duration-200 text-xs ";

                      if (showResult) {
                        if (isCorrect) {
                          optionClass +=
                            "bg-green-100 border-green-500 text-green-800";
                        } else if (isSelected && !isCorrect) {
                          optionClass +=
                            "bg-red-100 border-red-500 text-red-800";
                        } else {
                          optionClass +=
                            "bg-gray-50 border-gray-200 text-gray-600";
                        }
                      } else {
                        optionClass += isSelected
                          ? "bg-blue-100 border-blue-500 text-blue-800"
                          : "bg-white border-gray-200 hover:bg-blue-50";
                      }

                      return (
                        <div
                          key={idx}
                          className={optionClass}
                          onClick={() =>
                            handleQuizAnswer(slideId, idx, slide.quiz)
                          }
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <span className="font-medium mr-2 text-sm">
                                {String.fromCharCode(65 + idx)}.
                              </span>
                              <span className="leading-relaxed">{option}</span>
                            </div>
                            {showResult && isCorrect && (
                              <span className="text-green-600 font-bold text-base">
                                ✓
                              </span>
                            )}
                            {showResult && isSelected && !isCorrect && (
                              <span className="text-red-600 font-bold text-base">
                                ✗
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {showFeedback[slideId] && slide.quiz?.explanation && (
                    <div className="mt-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg flex-shrink-0">
                      <div className="font-semibold text-blue-800 mb-1.5 text-xs">
                        {quizAnswers[slideId] === slide.quiz.correctAnswer
                          ? `✅ ${t('carousel.quiz.correct')}`
                          : `❌ ${t('carousel.quiz.incorrect')}`}
                      </div>
                      <div className="text-blue-700 text-xs mb-1.5 leading-relaxed">
                        {slide.quiz.explanation}
                      </div>
                      <div className="text-xs text-blue-600 border-t border-blue-200 pt-1.5">
                        📖 {t('carousel.quiz.source_label')}:{" "}
                        {post.source_ref || slide.source_ref || "Document"}
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {slide.tags && slide.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {slide.tags.slice(0, 3).map((tag, idx) => (
              <span
                key={idx}
                className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  function showUserProfile() {
    if (setCurrentTab) {
      setCurrentTab("profile");
    }
    if (setShowSettings) {
      setShowSettings(false);
    }
  }

  function showUserChapter() {
    if (setCurrentTab) {
      setCurrentTab("chapters");
    }
    if (setShowSettings) {
      setShowSettings(false);
    }
  }

  return (
    <article className="bg-white border border-gray-100 rounded-lg overflow-hidden shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <div
          className="flex items-center gap-3 cursor-pointer hover:opacity-80"
      
        >
          <Avatar className="w-10 h-10"     onClick={showUserProfile}>
            <AvatarImage
              src={enhancedEntity?.avatar_url}
              alt={enhancedEntity?.name}
            />
            <AvatarFallback>
              {enhancedEntity?.name?.charAt(0) || "U"}
            </AvatarFallback>
          </Avatar>
          <div onClick={showUserChapter}>
            <div className="flex items-center gap-1">
              <h3 className="font-semibold text-sm">
                {enhancedEntity?.name || t('carousel.character_fallback')}
              </h3>
              {enhancedEntity?.verified && (
                <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs">✓</span>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500">
              {formatTime(post.created_at)}
            </p>
          </div>
        </div>
        <div className="relative">
          <button
            className="text-gray-600 hover:text-gray-800 p-1"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Toggle menu visibility and log for debug
              console.log("Three dots clicked: toggling post menu");
              setShowPostMenu((v) => !v);
            }}
            aria-haspopup="menu"
            aria-expanded={showPostMenu}
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>
          {showPostMenu && (
            <div className="absolute right-0 mt-2 z-[10010]">
              <ContextMenu
                options={[
                  { id: "view-source", label: t('carousel.view_source'), icon: BookOpen },
                  {
                    id: "toggle-save",
                    label: saved ? t('carousel.unsave') : t('carousel.save'),
                    icon: Bookmark,
                  },
                  // { id: "report", label: "Report", icon: Flag },
                  // Only show delete for non-demo content (demo user ID: d3000000-0000-0000-0000-000000000000)
                  ...(post.user_id !== 'd3000000-0000-0000-0000-000000000000' ? [{
                    id: "delete",
                    label: isDeleting ? `${t('common.loading')}…` : t('common.delete'),
                    icon: Trash2,
                    className: "text-red-600 hover:bg-red-50",
                    disabled: isDeleting,
                  }] : []),
                ].filter(Boolean)}
                onAction={(id) => {
                  if (id === "view-source") {
                    setSourceModalOpen(true);
                  } else if (id === "toggle-save") {
                    setSaved(!saved);
                    onSave?.(post.id, !saved);
                  } else if (id === "report") {
                    console.log("Report clicked for post", post.id);
                  } else if (id === "delete") {
                    setShowPostMenu(false);
                    setShowDeleteConfirm(true);
                    return;
                  }
                  setShowPostMenu(false);
                }}
                onClose={() => setShowPostMenu(false)}
              />
            </div>
          )}
        </div>
      </div>

      {/* Instagram-Style Carousel - 3:4 Aspect Ratio (Portrait) */}
      <div className="relative bg-black" style={{ aspectRatio: "3/4" }}>
        {/* Slides Container */}
        <div
          ref={scrollRef}
          className="flex overflow-x-auto scrollbar-hide h-full snap-x snap-mandatory"
          style={{
            scrollBehavior: "smooth",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {slides.map((slide, index) => (
            <div
              key={slide.id || index}
              className="w-full h-full flex-shrink-0 snap-center"
            >
              <SlideContent slide={slide} slideIndex={index} />
            </div>
          ))}
        </div>

        {/* Top-right slide counter */}
        {totalSlides > 1 && (
          <div className="absolute top-4 right-4 bg-black bg-opacity-70 text-white px-3 py-1 rounded-full text-sm font-medium">
            {currentSlide + 1}/{totalSlides}
          </div>
        )}

        {/* Bottom pagination dots */}
        {totalSlides > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentSlide ? "bg-white" : "bg-white bg-opacity-50"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Post Content */}
      <div className="p-4">
        {/* Action buttons */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-4">
            <button
              onClick={handleLike}
              className={`flex items-center gap-1 transition-colors ${
                liked ? "text-red-500" : "text-gray-600 hover:text-red-500"
              }`}
            >
              <Heart className={`w-6 h-6 ${liked ? "fill-current" : ""}`} />
            </button>

            <button
              onClick={() => onComment?.(post.id)}
              className="text-gray-600 hover:text-blue-500 transition-colors"
            >
              <MessageCircle className="w-6 h-6" />
            </button>

            <button className="text-gray-600 hover:text-gray-800 transition-colors">
              <Send className="w-6 h-6" />
            </button>
          </div>

          <button
            onClick={handleSave}
            className={`transition-colors ${
              saved ? "text-yellow-600" : "text-gray-600 hover:text-yellow-600"
            }`}
          >
            <Bookmark className={`w-6 h-6 ${saved ? "fill-current" : ""}`} />
          </button>
        </div>

        {/* Source reference - Shows actual page and line from Story 1.6.2 */}
        <div className="mb-2">
          {post.source_ref ||
          post.source_page_start ||
          post.page_number ||
          (slides.length > 0 && slides.some((slide) => slide.quiz)) ? (
            <button
              className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full inline-flex items-center gap-1 hover:bg-blue-100 cursor-pointer transition-colors"
              onClick={() => {
                // Show actual source references from Story 1.6.2
                const sourceInfo = [];

                // Collect source references from post data
                if (post.source_ref) {
                  sourceInfo.push(`📖 Primary Source: ${post.source_ref}`);
                }

                if (post.source_page_start && post.source_line_start) {
                  const chapter =
                    post.chapter_index || post.chapter_number || 1;
                  sourceInfo.push(
                    `📄 Location: Ch. ${chapter} - Page ${post.source_page_start} - Line ${post.source_line_start}`
                  );
                }

                if (post.source_confidence_score !== undefined) {
                  sourceInfo.push(
                    `🎯 Confidence: ${Math.round(
                      post.source_confidence_score * 100
                    )}%`
                  );
                }

                if (post.chapter_title) {
                  sourceInfo.push(`📚 Chapter: ${post.chapter_title}`);
                }

                if (post.section_title) {
                  sourceInfo.push(`📝 Section: ${post.section_title}`);
                }

                // Show source references from slides as well
                const slideReferences = slides.filter(
                  (slide) => slide.source_ref || slide.quiz?.source_ref
                );
                if (slideReferences.length > 0) {
                  sourceInfo.push(
                    `📊 Slide Sources: ${slideReferences.length} reference(s)`
                  );
                  slideReferences.forEach((slide, idx) => {
                    const ref = slide.source_ref || slide.quiz?.source_ref;
                    if (ref) {
                      sourceInfo.push(`  ${idx + 1}. ${ref}`);
                    }
                  });
                }

                setSourceModalOpen(true);
              }}
            >
              <span className="font-medium">📖</span>
              <span>
                {/* Show Ch. X - page Z - line Y format with sentinel-aware fallbacks (mirror FeedPost) */}
                {(() => {
                  const chapter =
                    post.chapter_index || post.chapter_number || 1;

                  // Priority 1: Use source_ref if present (usually the most accurate)
                  // But filter out bad patterns
                  if (post.source_ref) {
                    // Filter out sentinel patterns
                    if (
                      !post.source_ref.includes("Lines 1-21") &&
                      !post.source_ref.includes("Line 1-21") &&
                      !(
                        post.source_ref.includes("Page 1") &&
                        post.source_ref.includes("Lines 1-")
                      )
                    ) {
                      return post.source_ref;
                    }
                  }

                  // Priority 2: Use page/line when available (null values mean no line data)
                  const hasRealPageData =
                    post.source_page_start && post.source_page_start > 0;
                  // Filter out sentinel values (1-21 pattern)
                  const isSentinelData =
                    post.source_line_start === 1 && post.source_line_end === 21;
                  const hasRealLineData =
                    post.source_line_start !== null &&
                    post.source_line_start !== undefined &&
                    !isSentinelData;

                  if (
                    hasRealPageData &&
                    hasRealLineData &&
                    post.source_line_start &&
                    post.source_line_end
                  ) {
                    return `Ch. ${chapter} - Page ${post.source_page_start} - Lines ${post.source_line_start}-${post.source_line_end}`;
                  } else if (hasRealPageData && post.source_line_start) {
                    return `Ch. ${chapter} - Page ${post.source_page_start} - Line ${post.source_line_start}`;
                  }

                  // Priority 3: Other real data sources
                  if (post.line_number && post.page_number) {
                    return `Ch. ${chapter} - Page ${post.page_number} - Line ${post.line_number}`;
                  } else if (post.page_number && post.page_number !== 1) {
                    return `Ch. ${chapter} - Page ${post.page_number}`;
                  } else if (
                    post.position_data?.estimated_page &&
                    post.position_data?.line
                  ) {
                    return `Ch. ${chapter} - Page ${post.position_data.estimated_page} - Line ${post.position_data.line}`;
                  }

                  // Final fallback
                  return `Ch. ${chapter} - Document reference`;
                })()}
              </span>
            </button>
          ) : (
            <span className="font-semibold text-sm">
              {t('carousel.likes_count', { count: post.likes || 0 })}
            </span>
          )}
        </div>

        {/* Post title and content */}
        <div className="mb-2">
          <span className="font-semibold text-sm mr-2">
            {enhancedEntity?.name || t('carousel.entity_fallback')}
          </span>
          <span
            className="text-sm cursor-pointer hover:text-blue-600 transition-colors"
            onClick={() => {
              if (onProfileClick && entity) {
                onProfileClick(entity);
              }
            }}
          >
            {getDisplayTitle()}
          </span>
        </div>
      </div>

      {/* Source Info Modal */}
      <SourceInfoModal
        isOpen={sourceModalOpen}
        onClose={() => setSourceModalOpen(false)}
        post={post}
      />

      {/* Delete Confirm Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md mx-4 rounded-2xl">
          <DialogHeader>
            <DialogTitle>{t('carousel.delete_confirm_title')}</DialogTitle>
            <DialogDescription>
              {t('carousel.delete_confirm_desc')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleDeletePost}
              disabled={isDeleting}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? `${t('common.loading')}…` : t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </article>
  );
};

export default InstagramCarousel;
